import { useState } from 'react';

function DatasetBrowser({ onSelectDataset }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSource, setSelectedSource] = useState('all');
    const [selectedTask, setSelectedTask] = useState('all');

    // Mock datasets - will integrate with HuggingFace API
    const mockDatasets = [
        {
            id: 'imdb',
            name: 'IMDB Movie Reviews',
            source: 'huggingface',
            task: 'sentiment-analysis',
            samples: '25k',
            language: 'English',
            rating: 5,
            downloads: '10M+',
            description: 'Large Movie Review Dataset for binary sentiment classification',
            tags: ['sentiment', 'nlp', 'classification']
        },
        {
            id: 'sst2',
            name: 'SST-2 (Stanford Sentiment)',
            source: 'huggingface',
            task: 'sentiment-analysis',
            samples: '70k',
            language: 'English',
            rating: 4,
            downloads: '5M+',
            description: 'Stanford Sentiment Treebank with binary labels',
            tags: ['sentiment', 'nlp', 'benchmark']
        },
        {
            id: 'yelp',
            name: 'Yelp Reviews',
            source: 'kaggle',
            task: 'sentiment-analysis',
            samples: '500k',
            language: 'English',
            rating: 4,
            downloads: '2M+',
            description: 'Yelp review dataset with 5-star ratings',
            tags: ['sentiment', 'reviews', 'rating']
        },
        {
            id: 'cifar10',
            name: 'CIFAR-10',
            source: 'huggingface',
            task: 'image-classification',
            samples: '60k',
            language: 'N/A',
            rating: 5,
            downloads: '50M+',
            description: '60k 32x32 color images in 10 classes',
            tags: ['computer-vision', 'classification', 'benchmark']
        },
        {
            id: 'imagenet',
            name: 'ImageNet',
            source: 'huggingface',
            task: 'image-classification',
            samples: '14M',
            language: 'N/A',
            rating: 5,
            downloads: '100M+',
            description: 'Large scale image database organized according to WordNet hierarchy',
            tags: ['computer-vision', 'classification', 'large-scale']
        },
        {
            id: 'squad',
            name: 'SQuAD 2.0',
            source: 'huggingface',
            task: 'question-answering',
            samples: '150k',
            language: 'English',
            rating: 5,
            downloads: '20M+',
            description: 'Stanford Question Answering Dataset v2.0',
            tags: ['qa', 'nlp', 'benchmark']
        }
    ];

    const filteredDatasets = mockDatasets.filter(dataset => {
        const matchesSearch = dataset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            dataset.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            dataset.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesSource = selectedSource === 'all' || dataset.source === selectedSource;
        const matchesTask = selectedTask === 'all' || dataset.task === selectedTask;
        return matchesSearch && matchesSource && matchesTask;
    });

    return (
        <div className="flex-1 overflow-auto bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-1">
                                📦 Dataset Discovery
                            </h2>
                            <p className="text-gray-600">
                                Browse and search datasets from HuggingFace, Kaggle, and more
                            </p>
                        </div>
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            Upload Custom
                        </button>
                    </div>

                    {/* Search and Filters */}
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search datasets..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                        </div>
                        <select
                            value={selectedSource}
                            onChange={(e) => setSelectedSource(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Sources</option>
                            <option value="huggingface">🤗 HuggingFace</option>
                            <option value="kaggle">📦 Kaggle</option>
                            <option value="uci">🎓 UCI ML</option>
                        </select>
                        <select
                            value={selectedTask}
                            onChange={(e) => setSelectedTask(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Tasks</option>
                            <option value="sentiment-analysis">Sentiment Analysis</option>
                            <option value="image-classification">Image Classification</option>
                            <option value="question-answering">Question Answering</option>
                            <option value="text-generation">Text Generation</option>
                            <option value="object-detection">Object Detection</option>
                        </select>
                    </div>
                </div>

                {/* AI Recommendation */}
                <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border-2 border-cyan-200 p-6">
                    <div className="flex items-start gap-4">
                        <div className="text-4xl">🔍</div>
                        <div className="flex-1">
                            <h3 className="font-bold text-gray-900 mb-2">Data Scout Recommends</h3>
                            <p className="text-sm text-gray-700 mb-3">
                                Based on your project goals, I recommend starting with <strong>IMDB Movie Reviews</strong>:
                            </p>
                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 mb-4">
                                <li>Clean, balanced binary sentiment classification</li>
                                <li>Standard benchmark for NLP sentiment tasks</li>
                                <li>Manageable size (25k samples) for fast iteration</li>
                                <li>Well-documented and widely used in research</li>
                            </ul>
                            <button className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                                See all 7 alternatives
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Dataset Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {filteredDatasets.map(dataset => (
                        <DatasetCard
                            key={dataset.id}
                            dataset={dataset}
                            onSelect={() => onSelectDataset(dataset)}
                        />
                    ))}
                </div>

                {filteredDatasets.length === 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                        <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-gray-600 mb-2">No datasets found</p>
                        <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
                    </div>
                )}
            </div>
        </div>
    );
}

function DatasetCard({ dataset, onSelect }) {
    const getSourceBadge = () => {
        const badges = {
            huggingface: { icon: '🤗', label: 'HuggingFace', color: 'bg-yellow-100 text-yellow-700' },
            kaggle: { icon: '📦', label: 'Kaggle', color: 'bg-blue-100 text-blue-700' },
            uci: { icon: '🎓', label: 'UCI ML', color: 'bg-purple-100 text-purple-700' }
        };
        const badge = badges[dataset.source] || badges.huggingface;
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                <span>{badge.icon}</span>
                {badge.label}
            </span>
        );
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow cursor-pointer group">
            <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-1 group-hover:text-blue-600">
                        {dataset.name}
                    </h3>
                    <div className="flex items-center gap-2 mb-2">
                        {getSourceBadge()}
                        <span className="text-xs text-gray-500">
                            {dataset.samples} samples
                        </span>
                        <span className="text-xs text-gray-500">•</span>
                        <span className="text-xs text-gray-500">
                            {dataset.downloads} downloads
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                        <svg
                            key={i}
                            className={`w-4 h-4 ${i < dataset.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                    ))}
                </div>
            </div>

            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                {dataset.description}
            </p>

            <div className="flex flex-wrap gap-2 mb-4">
                {dataset.tags.map(tag => (
                    <span
                        key={tag}
                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                    >
                        #{tag}
                    </span>
                ))}
            </div>

            <div className="flex gap-2">
                <button
                    onClick={() => onSelect(dataset)}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center justify-center gap-2"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Load Dataset
                </button>
                <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
                    Preview
                </button>
                <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

export default DatasetBrowser;
